import { useCallback } from 'react'
import {
  CAPABILITY_NAME,
  MANUFACTURING_TYPE_ID_FABRIC,
  MANUFACTURING_TYPE_ID_FINISHED_GOOD,
  MANUFACTURING_TYPE_ID_PACKAGING,
  MANUFACTURING_TYPE_ID_RAWMATERIAL,
  RFI_TYPE_LOCATION,
  RFI_TYPE_QUESTIONNAIRE,
  RFI_TYPE_SUPPLIER,
} from '../../../../common/commonConstants'
import { unifyAllMasterData } from '../../../../common/helper'
import {
  getApprovedCapabilities,
  getDynamicMasterData,
  getMasterData,
  getRfi3Data,
  getRfiData,
  getRfiTemplate,
  getUFLPAMatch,
} from '../../../../services/lcmService'
import { TYPES as DT } from '../context/DataContext'
import { TYPES as UT } from '../context/UiContext'
import { buildTabUidMap } from '../utils/tabUtils'
import { resolveTemplateMapper } from '../utils/templateMapper'

export default function useRFIFetchAll({
  isReadonlyProp,
  supplierId,
  userId,
  rfiType,
  manufacturingTypeId,
  locationId,
  dd,
  ud,
}) {
  const setDefaultTab = useCallback(
    (template) => {
      const first = template?.dynamic_sections?.tabs?.[0]?.tab_name || ''
      dd({ type: DT.SET_ACTIVE_TAB, payload: first })
    },
    [dd],
  )

  const computeAllSectionUids = useCallback(
    (template) => {
      const set = new Set()
      ;(template?.dynamic_sections?.tabs || []).forEach((tab) =>
        (tab.sections || []).forEach((s) => set.add(s.uid)),
      )
      dd({ type: DT.SET_ALL_SECTION_UIDS, payload: set })
    },
    [dd],
  )

  const setNbior = useCallback(
    (rfiObj) => {
      const nbior =
        (rfiObj?.importer_of_record?.nbior || '').toLowerCase() === 'yes'
      ud({ type: UT.SET_FLAGS, payload: { isNBIOR: nbior } })
    },
    [ud],
  )

  const fetchAll = useCallback(async () => {
    try {
      ud({ type: UT.SET_LOADING, payload: { fetch: true, fetchFailed: false } })
      ud({ type: UT.SET_READONLY_FLAGS, payload: !!isReadonlyProp })

      const isReadonly = !!isReadonlyProp
      const who = isReadonly ? supplierId : userId

      const isPackaging =
        Number(manufacturingTypeId) === Number(MANUFACTURING_TYPE_ID_PACKAGING)
      const isFinishedGood =
        Number(manufacturingTypeId) ===
        Number(MANUFACTURING_TYPE_ID_FINISHED_GOOD)

      const [
        template,
        rfiObj,
        master,
        dynMaster,
        matchingSupplier,
        capabilitiesOverview,
        rfi3PackagingData,
        questionnaireMasterData,
      ] = await Promise.all([
        getRfiTemplate(
          who,
          rfiType,
          manufacturingTypeId,
          locationId,
          isReadonly,
        ),
        getRfiData(who, rfiType, manufacturingTypeId, locationId, isReadonly),
        getMasterData(rfiType, manufacturingTypeId),
        Number(manufacturingTypeId) === Number(MANUFACTURING_TYPE_ID_FABRIC) ||
        Number(manufacturingTypeId) ===
          Number(MANUFACTURING_TYPE_ID_RAWMATERIAL)
          ? getDynamicMasterData()
          : {},
        isReadonly ? getUFLPAMatch(supplierId) : null,
        isReadonly && !isFinishedGood
          ? getApprovedCapabilities(
              rfiType === RFI_TYPE_LOCATION
                ? RFI_TYPE_LOCATION
                : RFI_TYPE_SUPPLIER,
              supplierId,
              locationId,
              manufacturingTypeId,
            ).catch(() => ({}))
          : {},
        isPackaging && isReadonly
          ? getRfi3Data(
              supplierId,
              locationId,
              manufacturingTypeId,
              true,
            ).catch(() => null)
          : Promise.resolve(null),
        isPackaging && isReadonly
          ? getMasterData(RFI_TYPE_QUESTIONNAIRE, manufacturingTypeId).catch(
              () => null,
            )
          : Promise.resolve(null),
      ])

      const isUFLPAEntity =
        manufacturingTypeId === MANUFACTURING_TYPE_ID_FABRIC &&
        matchingSupplier?.matched_suppliers !== undefined &&
        matchingSupplier?.matched_suppliers?.length > 0

      const packagingCapabilityMasterList =
        questionnaireMasterData?.rfi_questionnaire?.packaging_capabilities || []
      const enrichedPackagingCapabilityVettingStatus =
        isPackaging && isReadonly && packagingCapabilityMasterList.length > 0
          ? packagingCapabilityMasterList.map((capability) => {
              const currentStatus = (
                rfi3PackagingData?.capability_vetting_status || []
              ).find(
                (existing) => existing.primary_type === capability,
              )?.vetting_status
              return {
                type: CAPABILITY_NAME,
                primary_type: capability,
                vetting_status: currentStatus,
              }
            })
          : null

      const enrichedRfiObj = enrichedPackagingCapabilityVettingStatus
        ? {
            ...rfiObj,
            capability_vetting_status: enrichedPackagingCapabilityVettingStatus,
          }
        : rfiObj

      dd({ type: DT.SET_TEMPLATE, payload: template })
      dd({ type: DT.SET_DATA, payload: enrichedRfiObj })
      dd({ type: DT.SET_DATA_FROM_API, payload: rfiObj })
      dd({
        type: DT.SET_MASTER,
        payload: rfiObj?.master_data
          ? unifyAllMasterData(
              unifyAllMasterData(master, dynMaster),
              rfiObj.master_data,
            )
          : unifyAllMasterData(master, dynMaster),
      })

      dd({
        type: DT.SET_CAPABILITY_OVERVIEW,
        payload: capabilitiesOverview || {},
      })
      dd({ type: DT.SET_TAB_UID_MAP, payload: buildTabUidMap(template) })
      dd({
        type: DT.SET_TEMPLATE_MAPPER,
        payload: resolveTemplateMapper(rfiType, manufacturingTypeId),
      })
      computeAllSectionUids(template)
      setDefaultTab(template)
      setNbior(rfiObj)
      ud({
        type: UT.SET_FLAGS,
        payload: {
          isNonCompliant: !!rfiObj?.non_compliant,
          isArchived: !!rfiObj?.archived,
          isRenderReadOnlySection:
            !!rfiObj?.non_compliant || !!rfiObj?.archived || isReadonly,
          isUFLPA: isUFLPAEntity,
        },
      })
      ud({
        type: UT.SET_LOADING,
        payload: { fetch: false, fetchFailed: false },
      })
      ud({ type: UT.SET_SUBMIT_FAILED, payload: false })
      ud({ type: UT.SET_ACTION_FAILED, payload: false })
    } catch (e) {
      ud({ type: UT.SET_LOADING, payload: { fetch: false, fetchFailed: true } })
    }
  }, [
    isReadonlyProp,
    supplierId,
    userId,
    rfiType,
    manufacturingTypeId,
    locationId,
    dd,
    ud,
    setDefaultTab,
    computeAllSectionUids,
    setNbior,
  ])

  return { fetchAll }
}
